// ============================================
// PA Profile Page
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { paMenuItems } from './paNav';
import NotificationBell from '../../components/common/NotificationBell';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import { loadUser } from '../../redux/slices/authSlice';

const PAProfile = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const { success, error: showError } = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '', phone: '', dateOfBirth: '', shareCode: '', paAddress: '', hasDBS: true, email: '',
    });

    const fetchProfile = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/pa/profile');
            const p = res.data.data;
            setForm({
                name: p.name || '',
                phone: p.phone || '',
                dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : '',
                shareCode: p.shareCode || '',
                paAddress: p.paAddress || '',
                hasDBS: p.hasDBS !== false,
                email: p.email || '',
            });
        } catch (err) { console.error('Fetch profile error:', err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch('/pa/profile', form);
            success('Profile updated successfully!');
            dispatch(loadUser()); // Sync Redux state so Settings page shows updated phone
        } catch (err) {
            showError(err.response?.data?.message || 'Failed to update profile');
        } finally { setSaving(false); }
    };

    const fields = [
        { key: 'name', label: 'Full Name', type: 'text', placeholder: 'Enter your full name', required: true, icon: '👤' },
        { key: 'phone', label: 'Contact Number', type: 'tel', placeholder: '+44 7000...', required: true, icon: '📱' },
        { key: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true, icon: '📅' },
        { key: 'shareCode', label: 'Share Code (if have one)', type: 'text', placeholder: 'e.g. S123-456-789', required: false, icon: '🔑' },
        { key: 'paAddress', label: 'Address', type: 'text', placeholder: 'Enter your full address', required: true, icon: '🏠', full: true },
    ];

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative pb-20 sm:pb-0">
            <Sidebar items={paMenuItems} theme="violet" title="IdaCab PA" />
            <div className="flex-1 flex flex-col overflow-hidden">
                <DriverMobileHeader
                    user={user}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    settingsPath="/pa/settings"
                />

                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center justify-between px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-violet-600 to-purple-400 bg-clip-text text-transparent tracking-tighter">Complete Profile</h2>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-3xl mx-auto space-y-8">
                        {/* Profile Hero */}
                        <div className="relative bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 dark:from-violet-900/80 dark:via-purple-900/80 dark:to-indigo-950/80 p-8 sm:p-10 rounded-[2.5rem] text-white shadow-2xl shadow-violet-600/30 dark:shadow-black/50 border border-white/10 overflow-hidden">
                            <div className="absolute -right-16 -top-16 w-56 h-56 bg-white/5 rounded-full blur-3xl" />
                            <h3 className="text-xl font-black uppercase tracking-tight mb-2 relative z-10 flex items-center gap-3">
                                <span className="w-3 h-3 rounded-full bg-white/80 shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
                                Personal Details
                            </h3>
                            <p className="text-sm text-white/70 font-medium relative z-10">Keep your profile up to date for compliance and verification purposes.</p>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin" />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-10 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {fields.map((f) => (
                                        <div key={f.key} className={`flex flex-col gap-2 ${f.full ? 'sm:col-span-2' : ''}`}>
                                            <label className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">
                                                {f.label} {f.required && <span className="text-rose-500">*</span>}
                                            </label>
                                            <input
                                                type={f.type}
                                                value={form[f.key]}
                                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                                required={f.required}
                                                placeholder={f.placeholder || ''}
                                                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-800 dark:text-white outline-none focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 [color-scheme:light] dark:[color-scheme:dark]"
                                            />
                                        </div>
                                    ))}

                                    {/* Email (read-only) */}
                                    <div className="flex flex-col gap-2 sm:col-span-2">
                                        <label className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest ml-1">Email (read-only)</label>
                                        <div className="w-full px-5 py-3.5 rounded-2xl bg-slate-100 dark:bg-slate-800/30 border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-400 dark:text-slate-500 cursor-not-allowed">
                                            {form.email || 'Contact admin to change email'}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-8">
                                    <button type="submit" disabled={saving}
                                        className="w-full py-4 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-black uppercase tracking-widest text-xs transition-all shadow-xl shadow-violet-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale flex justify-center items-center h-[56px]">
                                        {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Save Profile'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </main>
            </div>
            <BottomNav items={paMenuItems} />
        </div>
    );
};

export default PAProfile;
